import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full bg-transparent border-b border-[#181816]/20 py-3.5 font-light text-lg placeholder:text-[#8A857D]/70 focus:border-[#181816] transition-colors duration-300";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const sendReset = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setForgotBusy(false);
    }
  };

  useEffect(() => {
    if (user) navigate("/admin/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="admin-back-to-site" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8A857D] hover:text-[#181816] transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
        <div className="mt-10 border hairline bg-white/60 backdrop-blur px-8 sm:px-10 py-12">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">Studio Portal</p>
          <h1 className="mt-3 font-serif text-3xl sm:text-4xl">Welcome back, Ak.</h1>
          <form onSubmit={submit} className="mt-10 flex flex-col gap-7" data-testid="admin-login-form">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              data-testid="admin-email-input"
              className={inputCls}
              autoComplete="email"
            />
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="admin-password-input"
              className={inputCls}
              autoComplete="current-password"
            />
            {error && <p className="text-sm text-[#B85D3B]" data-testid="admin-login-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-button"
              className="w-full bg-[#181816] text-[#F8F6F0] py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#B85D3B] disabled:opacity-50 transition-colors duration-300"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="mt-6 border-t hairline pt-5">
            {!forgotOpen ? (
              <button
                onClick={() => setForgotOpen(true)}
                data-testid="admin-forgot-link"
                className="link-underline font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A857D] hover:text-[#181816] transition-colors"
              >
                Forgot password?
              </button>
            ) : forgotSent ? (
              <p className="font-light text-sm text-[#57534E]" data-testid="admin-forgot-sent">
                If that email is registered, a reset link is on its way. Check your inbox — the link works for one hour.
              </p>
            ) : (
              <form onSubmit={sendReset} className="flex flex-col gap-3" data-testid="admin-forgot-form">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A857D]">Reset by email</span>
                <input
                  required
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Your login email"
                  data-testid="admin-forgot-email-input"
                  className={inputCls}
                  autoComplete="email"
                />
                <button
                  type="submit"
                  disabled={forgotBusy}
                  data-testid="admin-forgot-submit"
                  className="w-fit border border-[#181816]/20 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#181816] disabled:opacity-50 transition-colors"
                >
                  {forgotBusy ? "Sending…" : "Email me a reset link"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
